/**
 * LaunchPage.jsx — Launch workspace UI (WP4, docs/launch-signoff-spec.md §7,
 * Track B part 3).
 *
 * Flow: paste a feature's acceptance criteria → "Compile criteria" maps each
 * one to an existing check / an AI-authored check / a manual procedure (WP3,
 * `POST /proxy/launches/<id>/compile`) → "Run checks" evaluates every mapped
 * check against captured traffic → results render grouped BY CRITERION, with
 * a worst-of rollup status (Met / Not met / Needs review / Manual review /
 * Not run — see utils/launchRollup.js) and a "Copy ticket" action on any
 * Not-met criterion.
 *
 * Scope resolution (which rows a compile/run call evaluates) is TWO layers,
 * applied in order — see ScopePanel below:
 *   1. base rows: the Dashboard's row-picker handoff
 *      (TrafficContext.checksScopeRows) when present and not switched off,
 *      else every captured request (liveTrafficGroups flattened);
 *   2. this page's own request-type / host filter.
 * Layer 2 exists because layer 1 alone left the page with no scope control of
 * its own: with no handoff it silently verified against the entire session, so
 * a criterion about one vendor was graded on every vendor's traffic. The HOST
 * facet is the load-bearing one — requestType classifies integration shape, so
 * Media.net's Prebid Server and the Xandr Prebid SDK are both 'prebid' and only
 * the host separates them. The picked types/hosts also travel to the compiler
 * as an authoritative scope directive (api/launches.js compileLaunch's
 * scopeTypes/scopeHosts), so authored checks carry the same narrowing the
 * tester chose here.
 *
 * App selection for a NEW launch mirrors ChecksPage's app dropdown default
 * (getActiveSelection().appId); once a launch is saved, its own `app` field is
 * what every subsequent compile/run call for that launch uses.
 *
 * Run results are persisted per launch in sessionStorage (runStateKey below):
 * clicking an evidence row navigates to the dashboard, which unmounts this
 * view, and without persistence every criterion came back reading "Not run".
 *
 * IMPORTANT finding (see api/checks.js runChecks() doc comment for the full
 * writeup): `/proxy/checks/run`'s `suite` and `checks` params do NOT merge —
 * passing a non-empty `checks` array makes the engine ignore the named
 * suite's stored checks entirely. This used to matter here because a
 * launch's mapped checks lived in TWO places: authored checks persisted to
 * the launch's own `launch-<id>` suite (WP3), and "existing" checks that
 * just cite an id living in one of the two builtin suites (`adtech-baseline`
 * / `generic` — the only two committed suites, see
 * proxy-server/conformance/suites/).
 *
 * As of the compile-time-copy polish (proxy-server/conformance/
 * compileCriteria.js §5), compiling a launch now copies the FULL definition
 * of every 'existing'-mapped check — under its ORIGINAL id — into the
 * launch's own suite alongside the authored checks. So the launch's own
 * suite alone covers every mapped criterion end-to-end, and the primary run
 * below (below, `covered`) resolves every checkId a launch's criteria cite.
 * `runLaunchChecks` still issues up to two supplemental, unrecorded lookup
 * calls against the builtin suites (`record:false`) — gated on `missing`,
 * whatever checkId the primary run did NOT cover — but this is now a LEGACY
 * FALLBACK: it exists for launches compiled before this polish shipped
 * (their suite predates the copy step and still only has authored checks),
 * and it naturally no-ops for anything compiled after, since `missing` comes
 * back empty. A checkId an "existing" mapping cites from some OTHER user
 * suite (not builtin, not this launch's own) still won't resolve from here;
 * its criterion just reads "Not run" rather than crashing or silently
 * guessing — see criterionStatus().
 *
 * No report-generation button yet — WP5 (buildLaunchReport.js) adds that;
 * see the placeholder comment in the Run section below.
 *
 * AUDITABILITY (the "what did Re-compile actually do?" pass). A compiled
 * criterion used to show an id, a confidence score and a verdict — nothing
 * about what the check GRADED or ASSERTED, which made a "Met" impossible to
 * audit and a mis-scoped compile impossible to fix without re-rolling the
 * model. Three things close that:
 *   - GET /proxy/launches/<id>/checks supplies the full Check definitions
 *     (`checkDefs` below); each check block renders them via CheckSpecPanel and
 *     can save an edit back to the launch's own suite;
 *   - `criterion.coverage` (compileCriteria.js coverageOf) names request
 *     families the criterion asked about that NOTHING mapped to it grades —
 *     rendered as a "Partly verified" warning on the card, because it changes
 *     what the verdict means;
 *   - the evidence table distinguishes an absence check's legitimately empty
 *     read ("none — clean") from a value check that read nothing at all, which
 *     is a vacuous pass and is called out as one.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import {
  listLaunches,
  getLaunch,
  saveLaunch,
  deleteLaunch,
  compileLaunch,
  getLaunchChecks,
  getLaunchRollup,
  recordLaunchRun,
  markLaunchReview,
  getReviewSummary,
  waiveUnit,
  waiveErrorText,
  rollupFlags,
} from '../api/launches';
import UnitRows from '../components/launch/UnitRows';
import WaiveDialog from '../components/launch/WaiveDialog';
import { WORDING } from '../utils/launchWording';
import { reviewTimeLabel, reviewMedianLabel, reviewWithinTarget } from '../utils/reviewTime';
import { runChecks, appendCheckToSuite } from '../api/checks';
import { saveHarToServer, getHar } from '../api/harClient';
import {
  persistRunEvidence,
  criterionEvidence,
  buildEvidenceHar,
  sliceEvidenceHar,
  evidenceFilename,
  downloadHarFile,
} from '../utils/evidenceHar';
import { getAllApps, getActiveSelection } from '../config/appProfiles';
import { useTraffic } from '../context/TrafficContext';
import { buildFindingTicket } from '../utils/buildFindingTicket';
import {
  applyScope,
  facetCounts,
  hostOfRow,
  typeOfRow,
  platformOfRow,
  deriveScopeDirective,
} from '../utils/launchScope';
import CapturePanel from '../components/launch/CapturePanel';
import SavedHarPicker from '../components/launch/SavedHarPicker';
import RunScopeNotes from '../components/launch/RunScopeNotes';
import DigestCard from '../components/launch/DigestCard';
import CheckResultView from '../components/CheckResultView';
import { copyTextToClipboard } from '../utils/clipboard';
import { manualEvidenceLine, unmappedEvidenceLine } from '../utils/evidenceLine';
import { openLaunchReport, downloadLaunchReport } from '../utils/buildLaunchReport';
import { loadReportLifecycles, winnersByGamRow } from '../utils/reportWinners';
import { postLifecycleGroups } from '../api/lifecycle';
import { buildSlotLifecycles } from '../components/AdLifecycleView';
import {
  criterionResults,
  CRITERION_STATUS,
  CRITERION_STATUS_LABEL,
  selectRollup,
} from '../utils/launchRollup';

const EMPTY_ROWS = [];

// WP6 (docs/mcp-signoff-spec.md §9): the criterion/launch VERDICT itself no
// longer comes from criterionStatus()/launchRollup() here — it comes from the
// server (GET /proxy/launches/<id>/rollup, api/launches.js getLaunchRollup()).
// criterionResults() survives because it is a pure display helper (checkId ->
// local result lookup for the evidence table), not a verdict computation.

// sessionStorage key for the currently-open launch id (WP1-style state
// recovery — round trip to the dashboard via a deep-link jump and back
// restores the detail view). Same storage tier ChecksPage uses for its Ask
// thread: tab-scoped, clears itself when the tab closes.
const SELECTED_LAUNCH_KEY = 'launch.selectedId';
const AD_ONLY_KEY = 'launch.adOnly';

// Per-launch sessionStorage keys.
//
// `launch.run.<id>` fixes a real reporting bug, not a convenience gap: run
// results lived in component state only, so following an evidence row into the
// dashboard (jumpToRequest → navigate('/')) unmounted LaunchDetail and threw
// them away. Coming back, every criterion re-rendered as "Not run" — the page
// claiming the tester never ran anything moments after they did. Inspecting the
// evidence is a normal step in verifying a criterion, so the state has to
// survive it. sessionStorage (tab-scoped, same tier as SELECTED_LAUNCH_KEY)
// because a run belongs to the session that produced it, not forever.
//
// `launch.scope.<id>` persists the request scope picked for that launch, so the
// same round trip doesn't silently re-widen a compile/run back to the whole
// session.
const runStateKey = (launchId) => `launch.run.${launchId}`;
const scopeKey = (launchId) => `launch.scope.${launchId}`;

// Cap on the persisted run payload. Evidence rows carry URLs, so a big suite
// over a big session can be large; sessionStorage is a ~5MB budget shared with
// everything else in the tab. Over the cap we persist the results WITHOUT
// evidence rows rather than dropping the run: statuses are what the criterion
// verdicts are computed from, and a rollup that survives beats evidence that
// doesn't.
const RUN_STATE_MAX_BYTES = 1_500_000;

function readRunState(launchId) {
  try {
    const raw = sessionStorage.getItem(runStateKey(launchId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.results)) return null;
    return parsed;
  } catch {
    return null; // corrupt/unavailable — behave as if nothing was stored
  }
}

function writeRunState(launchId, state) {
  try {
    const payload = JSON.stringify(state);
    if (payload.length <= RUN_STATE_MAX_BYTES) {
      sessionStorage.setItem(runStateKey(launchId), payload);
      return;
    }
    const slim = {
      ...state,
      evidenceDropped: true,
      results: (state.results || []).map((r) => {
        const rest = { ...r };
        delete rest.rows;
        delete rest.gradedIds;
        delete rest.failedIds;
        return rest;
      }),
    };
    sessionStorage.setItem(runStateKey(launchId), JSON.stringify(slim));
  } catch { /* quota/storage unavailable — the in-memory run still renders */ }
}

function clearRunState(launchId) {
  try { sessionStorage.removeItem(runStateKey(launchId)); } catch { /* ignore */ }
}

function readScopeState(launchId) {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(scopeKey(launchId)) || 'null');
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      types: Array.isArray(parsed.types) ? parsed.types : [],
      hosts: Array.isArray(parsed.hosts) ? parsed.hosts : [],
      useHandoff: parsed.useHandoff !== false,
    };
  } catch {
    return null;
  }
}

function writeScopeState(launchId, state) {
  try { sessionStorage.setItem(scopeKey(launchId), JSON.stringify(state)); } catch { /* ignore */ }
}

// ── status → color/label maps ───────────────────────────────────────────────
const MAPPING_COLOR = { existing: 'info', authored: 'success', manual: 'warning', unmapped: 'default' };
const MAPPING_LABEL = { existing: 'Existing check', authored: 'Authored check', manual: 'Manual', unmapped: 'Unmapped' };

const CRITERION_STATUS_COLOR = {
  [CRITERION_STATUS.MET]: 'success',
  [CRITERION_STATUS.NOT_MET]: 'error',
  [CRITERION_STATUS.NEEDS_REVIEW]: 'warning',
  [CRITERION_STATUS.MANUAL_REVIEW]: 'info',
  [CRITERION_STATUS.NOT_RUN]: 'default',
};

// Builtin suites the run/lookup logic falls back to for "existing"-mapped
// checkIds (see file header comment) — the only two committed suites
// (proxy-server/conformance/suites/*.checks.json). Legacy fallback only:
// launches compiled after the compile-time-copy polish carry these checks
// in their own suite already, so `missing` (below) comes back empty and this
// lookup naturally no-ops.
const BUILTIN_SUITES = ['adtech-baseline', 'generic'];

// ─────────────────────────────────────────────────────────────────────────
// ScopePanel — WHICH requests this launch is verified against.
//
// Before this existed the Launch page had no scope control at all: it took the
// Dashboard's row-picker handoff if one happened to be present, and otherwise
// silently used every captured request. So "review the Prebid requests from
// Media.net" was compiled and run against the whole session — including every
// other vendor's Prebid traffic, which is exactly the wrong answer to that
// question. The host facet is the one that matters: requestType classifies the
// integration shape, so several vendors share 'prebid', and the host is what
// tells them apart.
// ─────────────────────────────────────────────────────────────────────────
function ScopePanel({ sessionRows, handoffRows, scope, onChange, scopedCount }) {
  const hasHandoff = handoffRows.length > 0;
  const usingHandoff = hasHandoff && scope.useHandoff;
  const baseRows = usingHandoff ? handoffRows : sessionRows;

  const typeOptions = useMemo(() => facetCounts(baseRows, typeOfRow), [baseRows]);
  const hostOptions = useMemo(() => facetCounts(baseRows, hostOfRow), [baseRows]);
  const platformOptions = useMemo(() => facetCounts(baseRows, platformOfRow), [baseRows]);

  const set = (patch) => onChange({ ...scope, ...patch });

/* One scope facet: a multi-select with type-ahead over its own options.
 *
 * A component rather than a render function because it holds filter state, and
 * a hook cannot live inside a function called during another component's
 * render. The two facets each keep their own query — a shared one would filter
 * "Request type" by whatever was typed into "Host".
 *
 * The search box only appears past SEARCH_THRESHOLD options. Below that the
 * whole list is on screen and a search field is just another thing to read.
 */
const SEARCH_THRESHOLD = 8;

function ScopeFacet({ label, options, selected, onChange, idKey }) {
  const [query, setQuery] = React.useState('');

  const visible = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => String(o.value).toLowerCase().includes(q));
  }, [options, query]);

  // A selected option must stay rendered even when it does not match the
  // query: MUI reads its `value` list off the children, and a filtered-out
  // selection would silently drop from the chips the moment you typed.
  const shown = React.useMemo(() => {
    const seen = new Set(visible.map((o) => o.value));
    return visible.concat(options.filter((o) => selected.includes(o.value) && !seen.has(o.value)));
  }, [visible, options, selected]);

  return (
    <FormControl size="small" sx={{ minWidth: 210, maxWidth: 360, flex: 1 }}>
      <InputLabel id={`launch-scope-${idKey}`}>{label}</InputLabel>
      <Select
        multiple
        labelId={`launch-scope-${idKey}`}
        label={label}
        value={selected}
        onChange={(e) => onChange(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
        onClose={() => setQuery('')}
        renderValue={(vals) => (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {vals.map((v) => <Chip key={v} size="small" label={v} sx={{ fontFamily: 'monospace', fontSize: 11 }} />)}
          </Stack>
        )}
        MenuProps={{ PaperProps: { style: { maxHeight: 360 } }, autoFocus: false }}
      >
        {options.length > SEARCH_THRESHOLD && (
          // Not a MenuItem: MUI would treat it as an option and let the
          // keyboard select it. stopPropagation is required because Select
          // swallows keystrokes for its own type-ahead, so without it you can
          // type one character and the menu jumps somewhere else.
          <Box sx={{ px: 1, pt: 0.5, pb: 1 }} onKeyDown={(e) => e.stopPropagation()}>
            <TextField
              size="small" fullWidth autoFocus
              placeholder={`Filter ${options.length} ${label.toLowerCase()}…`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </Box>
        )}
        {options.length === 0 && <MenuItem disabled value="">(no requests captured)</MenuItem>}
        {options.length > 0 && shown.length === 0 && (
          <MenuItem disabled value="">no match for “{query}”</MenuItem>
        )}
        {shown.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            <Checkbox size="small" checked={selected.includes(o.value)} />
            <ListItemText
              primary={o.value}
              secondary={`${o.count} request${o.count === 1 ? '' : 's'}`}
              primaryTypographyProps={{ fontFamily: 'monospace', fontSize: 12 }}
            />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

  // Drop selections that the new base rows no longer offer, so a stale filter
  // can't silently scope everything down to zero.
  const pruneAgainst = (options, selected) => {
    const available = new Set(options.map((o) => o.value));
    return selected.filter((v) => available.has(v));
  };

  const renderFacet = (label, options, selected, key) => (
    <ScopeFacet
      label={label}
      options={options}
      selected={pruneAgainst(options, selected)}
      onChange={(vals) => set({ [key]: vals })}
      idKey={key}
    />
  );

  const platforms = Array.isArray(scope.platforms) ? scope.platforms : [];
  const filtered = scope.types.length > 0 || scope.hosts.length > 0 || platforms.length > 0;

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Requests to verify against</Typography>
        <Typography variant="caption" color="text.secondary">
          {scopedCount} of {baseRows.length} request{baseRows.length === 1 ? '' : 's'}
          {usingHandoff ? ' (from the Dashboard selection)' : ''}
        </Typography>
      </Stack>

      {hasHandoff && (
        <FormControlLabel
          control={<Switch size="small" checked={scope.useHandoff} onChange={(e) => set({ useHandoff: e.target.checked })} />}
          label={<Typography variant="caption">Start from the {handoffRows.length} request{handoffRows.length === 1 ? '' : 's'} picked on the Dashboard</Typography>}
        />
      )}

      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: hasHandoff ? 1 : 0 }}>
        {renderFacet('Platform', platformOptions, platforms, 'platforms')}
        {renderFacet('Request type', typeOptions, scope.types, 'types')}
        {renderFacet('Host (vendor)', hostOptions, scope.hosts, 'hosts')}
        {filtered && (
          <Button size="small" onClick={() => set({ types: [], hosts: [], platforms: [] })}>Clear filters</Button>
        )}
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {scope.hosts.length > 0
          ? `Compiled checks are scoped to ${scope.hosts.join(', ')} — request type alone would also grade other vendors on the same type.`
          : 'Pick a host when a criterion is about one vendor: several vendors share a request type (every Prebid Server is requestType "prebid"), so the host is what separates them.'}
      </Typography>

      {scopedCount === 0 && baseRows.length > 0 && (
        <Alert severity="warning" icon={false} sx={{ mt: 1, py: 0.5 }}>
          This filter matches no captured requests — compile and run would have nothing to look at.
        </Alert>
      )}
    </Paper>
  );
}

function MappingChip({ mapping }) {
  const m = mapping || 'unmapped';
  return <Chip size="small" label={MAPPING_LABEL[m] || m} color={MAPPING_COLOR[m] || 'default'} variant="outlined" sx={{ fontWeight: 600 }} />;
}

function CriterionStatusChip({ status }) {
  return (
    <Chip
      size="small"
      label={CRITERION_STATUS_LABEL[status] || status}
      color={CRITERION_STATUS_COLOR[status] || 'default'}
      sx={{ fontWeight: 700 }}
    />
  );
}

// Tester identity for the sign-off report header — same storage key the
// dashboard's auth flow writes on login (see api/client.js / LoginPage.jsx).
function readTester() {
  try { return localStorage.getItem('auth_user') || ''; } catch { return ''; }
}

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
}

/** Time only — for the source picker, where the date is almost always today
 * and the full stamp would push the labels off the row. */
function fmtTime(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString(); } catch { return ''; }
}

/** Is this a 409 { error: 'anthropic_api_key_missing' } rejection? Same gate
 * copy used across ChecksPage.jsx / BuildValidationPanel.jsx. */
function is409ApiKey(err) {
  return err?.status === 409 && err?.body?.error === 'anthropic_api_key_missing';
}

// ─────────────────────────────────────────────────────────────────────────
// CriterionCard
// ─────────────────────────────────────────────────────────────────────────
// One-line outcome of persistRunEvidence for the caption under the run
// controls. '' when the HAR saved and nothing is missing — success is the
// Download button appearing, not a message.
function evidenceStatusText(ev) {
  if (!ev || ev.error) return '';
  if (ev.harError === 'no graded requests') return 'No requests were graded, so no evidence HAR was saved.';
  if (ev.harError) return `Evidence HAR not saved (${ev.harError}) — downloads fall back to the rows this tab still holds.`;
  if (ev.missing > 0) return `Evidence HAR saved, but ${ev.missing} graded request${ev.missing === 1 ? ' was' : 's were'} no longer in this tab and ${ev.missing === 1 ? 'is' : 'are'} missing from it (listed in the HAR).`;
  return '';
}

function CriterionCard({ criterion, resultsByCheckId, sources = [], activeSourceKey, checkDefsById, onJump, onSaveNote, onSaveCheck, app, runId, launch, rowsByRequestId, suiteName, onDownloadEvidence, evidenceCounts, onWaive }) {
  const [note, setNote] = useState(criterion?.manualNote || '');
  const [savingNote, setSavingNote] = useState(false);
  const [copyState, setCopyState] = useState('idle'); // idle | copied | failed

  useEffect(() => { setNote(criterion?.manualNote || ''); }, [criterion?.manualNote, criterion?.id]);

  // checks[] (per-check evidence breakdown: status chip, matched/found, rows)
  // still reads the LOCAL result objects — the server rollup's checks[] is
  // slim ({checkId,status,reason?}, no evidence rows), so evidence rendering
  // stays sourced from the raw /proxy/checks/run response held in this tab.
  const checks = criterionResults(criterion, resultsByCheckId);

  // The criterion's overall VERDICT (met/not-met/needs-review/…), one per run
  // source that has produced a server-attached result — WP6: this is now the
  // proxy's own computation (GET /proxy/launches/<id>/rollup's `criteria[]`,
  // fetched per source by LaunchDetail into `sources[].serverRollup`), not a
  // local criterionStatus() call. With a single run this is just the status
  // chip; with two it is the whole point — a batch run and a live capture
  // that disagree must both be visible, each named, instead of one of them
  // quietly winning.
  const verdicts = sources.map((s) => {
    const entry = Array.isArray(s.serverRollup?.criteria)
      ? s.serverRollup.criteria.find((e) => e && e.criterionId === criterion?.id)
      : null;
    return { key: s.key, label: s.label, at: s.at, status: entry?.status || CRITERION_STATUS.NOT_RUN };
  });
  const activeVerdict = verdicts.find((v) => v.key === activeSourceKey) || verdicts[0];
  const status = activeVerdict?.status || CRITERION_STATUS.NOT_RUN;
  const disagree = verdicts.length > 1 && new Set(verdicts.map((v) => v.status)).size > 1;

  // T34 (E17, E28): the proxy's unit answer for the active source. `mode` and the unconfirmed ids come off the
  // rollup answer (rollupFlags); nothing is graded here. mode 'off' (or an older proxy) renders no unit rows.
  const activeServer = (sources.find((s) => s.key === activeSourceKey) || sources[0])?.serverRollup || null;
  const { mode: gradingMode, unconfirmed: unconfirmedIds } = rollupFlags(activeServer);
  const redesignEntry = Array.isArray(activeServer?.criteria)
    ? activeServer.criteria.find((e) => e && e.criterionId === criterion?.id)?.redesign || null
    : null;
  const branchLabels = {};
  for (const b of (Array.isArray(criterion?.branches) ? criterion.branches : [])) if (b && b.id) branchLabels[b.id] = b.label || b.id;
  for (const v of (Array.isArray(criterion?.scope?.variants) ? criterion.scope.variants : [])) if (v && v.id) branchLabels[v.id] = v.label || v.id;
  const criterionUnconfirmed = gradingMode === 'shadow' && (redesignEntry?.unconfirmed === true || unconfirmedIds.includes(criterion?.id));

  async function handleSaveNote() {
    setSavingNote(true);
    try { await onSaveNote(criterion.id, note); } finally { setSavingNote(false); }
  }

  function handleCopyTicket() {
    // First failing check's result is the representative finding for a
    // Not-met criterion (spec §8/§7) — fall back to the first check with any
    // result at all if somehow none carries 'fail' (defensive, shouldn't
    // happen given criterionStatus()'s rules).
    const failing = checks.find((c) => c.result?.status === 'fail') || checks.find((c) => c.result) || checks[0];
    let md = '';
    try {
      md = buildFindingTicket({
        check: { id: failing?.checkId, description: criterion?.text },
        result: failing?.result || {},
        app,
        runId,
        launch,
        rowsByRequestId,
        suiteName,
      });
    } catch { md = ''; }
    const ok = md ? copyTextToClipboard(md) : false;
    setCopyState(ok ? 'copied' : 'failed');
    setTimeout(() => setCopyState('idle'), 2000);
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" useFlexGap>
        <Box sx={{ flex: 1, minWidth: 260 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{criterion?.text || '(empty criterion)'}</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <MappingChip mapping={criterion?.mapping} />
          {verdicts.length > 1 ? (
            verdicts.map((v) => (
              <Stack key={v.key} direction="row" spacing={0.5} alignItems="center">
                <Typography variant="caption" color="text.secondary">{v.label}</Typography>
                <CriterionStatusChip status={v.status} />
              </Stack>
            ))
          ) : (
            <CriterionStatusChip status={status} />
          )}
        </Stack>
      </Stack>

      {criterionUnconfirmed && (
        <Typography variant="caption" data-testid="criterion-unconfirmed" sx={{ display: 'block', mt: 0.5, color: 'warning.main' }}>
          {WORDING.shadowLabel}
        </Typography>
      )}
      {redesignEntry && (
        <UnitRows
          units={redesignEntry.units}
          mode={gradingMode}
          criterionId={criterion?.id}
          labels={branchLabels}
          resultsByCheckId={resultsByCheckId}
          waivers={activeServer?.waivers}
          voided={activeServer?.voidedWaivers}
          onWaive={onWaive ? (unitId) => onWaive(criterion.id, unitId) : undefined}
        />
      )}

      {disagree && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'warning.main' }}>
          These runs disagree — they graded different traffic (the batch run uses the requests you
          selected; the live capture uses what the device is emitting now). Evidence below is from{' '}
          {(sources.find((s) => s.key === activeSourceKey) || sources[0])?.label}.
        </Typography>
      )}

      {typeof criterion?.confidence === 'number' && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          confidence: {Math.round(criterion.confidence * 100)}%
        </Typography>
      )}
      {criterion?.rationale && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {criterion.rationale}
        </Typography>
      )}

      {criterion?.mapping === 'manual' ? (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }} data-testid="evidence-line-manual">
            {manualEvidenceLine()}
          </Typography>
          <TextField
            label="Manual verification note"
            placeholder="How was this verified? (visual QA, screen recording, etc.)"
            fullWidth
            multiline
            minRows={2}
            size="small"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <Button size="small" sx={{ mt: 0.5 }} disabled={savingNote || note === (criterion?.manualNote || '')} onClick={handleSaveNote}>
            {savingNote ? 'Saving…' : 'Save note'}
          </Button>
        </Box>
      ) : criterion?.mapping === 'unmapped' ? (
        <Alert severity="info" icon={false} sx={{ mt: 1.5, py: 0.5 }}>
          {unmappedEvidenceLine()}{' '}
          Not mapped to any check yet — compile again, or edit this criterion's text to be more specific.
        </Alert>
      ) : (
        <Box sx={{ mt: 1.5 }}>
          {/* Coverage gap (compileCriteria.js coverageOf): the criterion names
              request families no mapped check targets. Shown next to the
              verdict because it CHANGES what the verdict means — "Met" on a
              criterion that says "prebid and GAM" while nothing graded GAM is
              half an answer, and it used to be invisible. */}
          {Array.isArray(criterion?.coverage?.missing) && criterion.coverage.missing.length > 0 && (
            <Alert severity="warning" icon={false} sx={{ mb: 1, py: 0.5 }}>
              <Typography variant="caption">
                <strong>Partly verified.</strong> This criterion names{' '}
                {criterion.coverage.named.join(' + ')}, but nothing mapped to it grades{' '}
                <strong>{criterion.coverage.missing.join(', ')}</strong> — that traffic was not checked.
                Re-compile with those requests in scope, or add a check for them.
              </Typography>
            </Alert>
          )}
          {Array.isArray(criterion?.baselineIds) && criterion.baselineIds.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              + {criterion.baselineIds.length} baseline check{criterion.baselineIds.length === 1 ? '' : 's'} attached
              (this criterion asks for whole-family conformance).
            </Typography>
          )}
          {checks.length === 0 && (
            <Typography variant="caption" color="text.secondary">No mapped checks.</Typography>
          )}
          {checks.map(({ checkId, result }) => (
            <Box key={checkId} sx={{ pl: 1, py: 0.5, borderLeft: '2px solid var(--line)' }}>
              <CheckResultView
                checkId={checkId}
                result={result}
                def={checkDefsById?.get?.(checkId) || null}
                onJumpToRequest={onJump}
                onSaveCheck={onSaveCheck ? (next) => onSaveCheck(next) : undefined}
                showSummary
                showEvidence
              />
            </Box>
          ))}
        </Box>
      )}

      {(status === CRITERION_STATUS.NOT_MET || (onDownloadEvidence && checks.length > 0 && status !== CRITERION_STATUS.NOT_RUN)) && (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
          {status === CRITERION_STATUS.NOT_MET && (
            <Button size="small" variant="outlined" onClick={handleCopyTicket}>
              {copyState === 'copied' ? 'Copied ✓' : copyState === 'failed' ? 'Copy failed' : 'Copy ticket'}
            </Button>
          )}
          {onDownloadEvidence && checks.length > 0 && status !== CRITERION_STATUS.NOT_RUN && (
            <>
              <Button size="small" variant="outlined" onClick={() => onDownloadEvidence(criterion.id, false)}>
                Download HAR{evidenceCounts ? ` (${evidenceCounts.graded})` : ''}
              </Button>
              {(!evidenceCounts || evidenceCounts.failed > 0) && (
                <Button size="small" onClick={() => onDownloadEvidence(criterion.id, true)}>
                  Failing only{evidenceCounts ? ` (${evidenceCounts.failed})` : ''}
                </Button>
              )}
            </>
          )}
        </Stack>
      )}
    </Paper>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Launch list
// ─────────────────────────────────────────────────────────────────────────
/**
 * filterLaunches(list, { text, verdict, autoOnly }) -> list. Free text matches
 * title / ticket / app / build / platform / date (ISO or locale), case-insensitive,
 * every whitespace-separated term must hit. `verdict` is 'pass'|'fail'|'unknown'
 * (the latest run's verdict; rows without runs have none). Never throws.
 */
export function filterLaunches(list, { text = '', verdict = '', autoOnly = false } = {}) {
  const terms = String(text || '').toLowerCase().split(/\s+/).filter(Boolean);
  return (Array.isArray(list) ? list : []).filter((l) => {
    if (!l) return false;
    if (autoOnly && !l.auto) return false;
    if (verdict && l.verdict !== verdict) return false;
    if (!terms.length) return true;
    let when = '';
    try { when = `${l.updatedAt || ''} ${l.updatedAt ? new Date(l.updatedAt).toLocaleDateString() : ''}`; } catch { /* ignore */ }
    const hay = `${l.title || ''} ${l.ticket || ''} ${l.app || ''} ${l.build || ''} ${l.platform || ''} ${when}`.toLowerCase();
    return terms.every((t) => hay.includes(t));
  });
}

export function LaunchList({ onOpen, onCreate }) {
  const [launches, setLaunches] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [filterVerdict, setFilterVerdict] = useState('');
  const [autoOnly, setAutoOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const refresh = useCallback(() => {
    setLoading(true);
    setError('');
    listLaunches()
      .then((list) => setLaunches(Array.isArray(list) ? list : []))
      .catch((e) => setError(e?.message || 'Could not load launches.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Launches</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={onCreate}>New launch</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {launches.length > 0 && (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <TextField
            size="small" placeholder="Search app, build, ticket, date" value={filterText}
            onChange={(e) => setFilterText(e.target.value)} sx={{ minWidth: 280 }}
            inputProps={{ 'aria-label': 'Search launches' }}
          />
          <TextField
            select size="small" label="Verdict" value={filterVerdict} sx={{ minWidth: 130 }}
            onChange={(e) => setFilterVerdict(e.target.value)}
          >
            <MenuItem value="">Any</MenuItem>
            <MenuItem value="pass">Pass</MenuItem>
            <MenuItem value="fail">Fail</MenuItem>
            <MenuItem value="unknown">Not evaluated</MenuItem>
          </TextField>
          <FormControlLabel
            control={<Checkbox size="small" checked={autoOnly} onChange={(e) => setAutoOnly(e.target.checked)} />}
            label="Auto only"
          />
        </Stack>
      )}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress size={28} /></Stack>
      ) : launches.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No launches yet. Paste a feature's acceptance criteria to start one.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Ticket</TableCell>
                <TableCell>App</TableCell>
                <TableCell>Build</TableCell>
                <TableCell align="center">Criteria</TableCell>
                <TableCell align="center">Runs</TableCell>
                <TableCell>Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filterLaunches(launches, { text: filterText, verdict: filterVerdict, autoOnly }).map((l) => (
                <TableRow key={l.id} hover sx={{ cursor: 'pointer' }} onClick={() => onOpen(l.id)}>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {l.title || '(untitled)'}
                    {l.auto && <Chip size="small" label="auto" variant="outlined" sx={{ ml: 1, height: 18 }} />}
                  </TableCell>
                  <TableCell>{l.ticket || '—'}</TableCell>
                  <TableCell>{l.app || '—'}</TableCell>
                  <TableCell>{l.build || '—'}</TableCell>
                  <TableCell align="center">{l.counts?.criteria ?? 0}</TableCell>
                  <TableCell align="center">{l.counts?.runs ?? 0}</TableCell>
                  <TableCell>{fmtDate(l.updatedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Create form
// ─────────────────────────────────────────────────────────────────────────
function CreateLaunchForm({ onCancel, onCreated }) {
  const appOptions = useMemo(() => { try { return Object.values(getAllApps() || {}); } catch { return []; } }, []);
  const [title, setTitle] = useState('');
  const [ticket, setTicket] = useState('');
  const [app, setApp] = useState(() => { try { return getActiveSelection()?.appId || ''; } catch { return ''; } });
  const [build, setBuild] = useState('');
  const [criteriaText, setCriteriaText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      const res = await saveLaunch({ title, ticket, app, build, criteriaText });
      if (res?.ok && res?.launch?.id) onCreated(res.launch.id);
      else setError('Save failed — malformed response from the proxy.');
    } catch (e) {
      setError(e?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton size="small" onClick={onCancel}><ArrowBackIcon fontSize="small" /></IconButton>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>New launch</Typography>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack spacing={2}>
        <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth size="small" />
        <TextField label="Ticket ref (e.g. JIRA key)" value={ticket} onChange={(e) => setTicket(e.target.value)} fullWidth size="small" />
        <Stack direction="row" spacing={2}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="launch-app-label">App</InputLabel>
            <Select labelId="launch-app-label" label="App" value={app} onChange={(e) => setApp(e.target.value)}>
              <MenuItem value="">(none)</MenuItem>
              {appOptions.map((a) => <MenuItem key={a?.id} value={a?.id}>{a?.name || a?.id}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Build" value={build} onChange={(e) => setBuild(e.target.value)} size="small" />
        </Stack>
        <TextField
          label="Acceptance criteria (paste, one per line)"
          value={criteriaText}
          onChange={(e) => setCriteriaText(e.target.value)}
          fullWidth
          multiline
          minRows={6}
        />
        <Stack direction="row" spacing={1}>
          <Button variant="contained" disabled={saving || !title.trim()} onClick={handleSave}>
            {saving ? 'Saving…' : 'Save launch'}
          </Button>
          <Button onClick={onCancel} disabled={saving}>Cancel</Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Detail view
// ─────────────────────────────────────────────────────────────────────────
function LaunchDetail({ launchId, onBack, onDeleted }) {
  const { liveTrafficGroups, checksScopeRows, setJumpTarget } = useTraffic();
  const navigate = useNavigate();

  const [launch, setLaunch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [criteriaTextDraft, setCriteriaTextDraft] = useState('');
  const [savingText, setSavingText] = useState(false);

  // Which half of the Verify panel is showing. Purely presentational — a
  // capture session keeps running while you read a batch result.
  const [verifyMode, setVerifyMode] = useState('batch');

  // Results from the live capture, lifted out of CapturePanel so both runs
  // live at the same level and neither can quietly become "the" answer. Used
  // for EVIDENCE only now (WP6) — the verdict/timestamp shown per source
  // comes from the server rollup (batchRollup/liveRollup below), since a
  // CapturePanel preview run (`record:false`) isn't attached to the launch
  // and shouldn't be able to move the displayed verdict.
  const [liveResults, setLiveResults] = useState([]);
  const handleLiveResults = useCallback((results) => {
    setLiveResults(Array.isArray(results) ? results : []);
  }, []);

  // ── WP6 (docs/mcp-signoff-spec.md §6/§9): the AUTHORITATIVE verdict,
  // fetched per-source from the proxy. Deliberately fetched as TWO separate
  // calls (`source=batch` and `source=live`) and never `source=merged` — see
  // the `sources` comment below for why the two stay unmerged.
  const [batchRollup, setBatchRollup] = useState(null);
  const [liveRollup, setLiveRollup] = useState(null);
  const [rollupsLoading, setRollupsLoading] = useState(false);

  const refreshRollups = useCallback(() => {
    setRollupsLoading(true);
    Promise.allSettled([
      getLaunchRollup(launchId, { source: 'batch', detail: 1 }),
      getLaunchRollup(launchId, { source: 'live', detail: 1 }),
    ])
      .then(([b, l]) => {
        setBatchRollup(b.status === 'fulfilled' ? b.value : null);
        setLiveRollup(l.status === 'fulfilled' ? l.value : null);
      })
      .finally(() => setRollupsLoading(false));
  }, [launchId]);

  useEffect(() => { refreshRollups(); }, [refreshRollups]);

  // Which run's evidence the criteria cards drill into. Null = follow the
  // freshest run; an explicit pick sticks until the tester changes it.
  const [activeSourceKey, setActiveSourceKey] = useState(null);

  const [compiling, setCompiling] = useState(false);
  const [compileError, setCompileError] = useState(null); // { message, is409, issues }
  const [compileNotes, setCompileNotes] = useState([]);   // issues[] from a SUCCESSFUL compile

  // The FULL definition of every check this launch's compile produced, keyed by
  // id (GET /proxy/launches/<id>/checks). The launch record stores only
  // checkIds, so without this the page can name a criterion's checks but not
  // show what they grade or assert — and a compile that scoped a check wrongly
  // can neither be seen nor fixed.
  const [checkDefs, setCheckDefs] = useState([]);
  const [checkSuite, setCheckSuite] = useState('');
  const checkDefsById = useMemo(() => {
    const m = new Map();
    for (const c of (Array.isArray(checkDefs) ? checkDefs : [])) {
      if (c && typeof c.id === 'string') m.set(c.id, c);
    }
    return m;
  }, [checkDefs]);

  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState('');
  // WP6 (docs/mcp-signoff-spec.md §9): attach-run failing used to be a silent
  // catch — the run still showed up locally (runResults), so nothing on
  // screen told the tester the verdict below might be stale. Now that the
  // DISPLAYED verdict comes from the server rollup (which only knows about
  // attached runs), a failed attach means this run is invisible to it, so
  // it must be surfaced, not swallowed.
  const [attachWarning, setAttachWarning] = useState('');
  // Evidence HAR (docs/ticket-validation-workflow-spec.md §4): save/download
  // problems — never blocks the verdict.
  const [evidenceMsg, setEvidenceMsg] = useState('');
  // T34 (E17): the unit a person is waiving, { criterionId, unitId } | null, and the last waive error.
  const [waiveTarget, setWaiveTarget] = useState(null);
  const [waiveError, setWaiveError] = useState('');
  const handleWaiveSubmit = useCallback(async (reason) => {
    if (!waiveTarget) return;
    setWaiveError('');
    try {
      await waiveUnit(launchId, { criterionId: waiveTarget.criterionId, branchId: waiveTarget.unitId, reason });
      setWaiveTarget(null);
      refreshRollups();
    } catch (e) {
      setWaiveError(waiveErrorText(e));
    }
  }, [launchId, waiveTarget, refreshRollups]);
  // Latest run, rehydrated from sessionStorage on mount so that following an
  // evidence row into the dashboard and coming back does NOT reset every
  // criterion to "Not run" (see runStateKey above).
  const launchRef = useRef(null);
  useEffect(() => { launchRef.current = launch; }, [launch]);
  const [runResults, setRunResults] = useState(() => readRunState(launchId)?.results || []);
  const [lastRunId, setLastRunId] = useState(() => readRunState(launchId)?.runId || null);
  const [runAt, setRunAt] = useState(() => readRunState(launchId)?.at || null);
  const [runEvidenceDropped, setRunEvidenceDropped] = useState(() => !!readRunState(launchId)?.evidenceDropped);

  const [adOnly, setAdOnly] = useState(() => { try { return localStorage.getItem(AD_ONLY_KEY) === 'true'; } catch { return false; } });
  useEffect(() => { try { localStorage.setItem(AD_ONLY_KEY, adOnly ? 'true' : 'false'); } catch { /* ignore */ } }, [adOnly]);

  const [reviewSummary, setReviewSummary] = useState(null);
  const refreshReviewSummary = useCallback(() => { getReviewSummary().then((r) => { if (r) setReviewSummary(r); }); }, []);
  // The human "signed" moment is generating/downloading the sign-off report.
  const markSigned = useCallback((id) => {
    markLaunchReview(id, 'sign').then((r) => {
      if (r?.review) setLaunch((cur) => (cur && cur.id === id ? { ...cur, review: r.review } : cur));
      refreshReviewSummary();
    });
  }, [refreshReviewSummary]);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError('');
    getLaunch(launchId)
      .then((l) => {
        if (!l) { setLoadError('Launch not found.'); setLaunch(null); return; }
        setLaunch(l);
        setCriteriaTextDraft(l.criteriaText || '');
        // Review-time measurement: first open starts the clock (server keeps the first stamp).
        markLaunchReview(l.id, 'start').then((r) => { if (r?.review) setLaunch((cur) => (cur && cur.id === l.id ? { ...cur, review: r.review } : cur)); });
        refreshReviewSummary();
      })
      .catch((e) => setLoadError(e?.message || 'Could not load this launch.'))
      .finally(() => setLoading(false));
  }, [launchId, refreshReviewSummary]);

  // Pull the compiled check definitions alongside the launch record. Failure is
  // non-fatal by design: the page still renders ids, verdicts and evidence — it
  // just can't show the spec — so a proxy that predates the /checks route
  // degrades instead of breaking the whole view.
  const loadCheckDefs = useCallback(() => {
    getLaunchChecks(launchId)
      .then(({ suite, checks }) => { setCheckSuite(suite || ''); setCheckDefs(checks || []); })
      .catch(() => { setCheckDefs([]); });
  }, [launchId]);

  useEffect(() => { load(); loadCheckDefs(); }, [load, loadCheckDefs]);
  // Switching launches swaps in THAT launch's stored run (or nothing) — the
  // previous launch's results must never bleed into another's criteria.
  useEffect(() => {
    const stored = readRunState(launchId);
    setRunResults(stored?.results || []);
    setLastRunId(stored?.runId || null);
    setRunAt(stored?.at || null);
    setRunEvidenceDropped(!!stored?.evidenceDropped);
    setRunError('');
    // The live capture is per-session and never stored, so it starts empty for
    // the new launch; the source pick goes back to "follow the freshest run"
    // rather than pointing at a source this launch may not even have.
    setLiveResults([]);
    setActiveSourceKey(null);
    setVerifyMode('batch');
    setAttachWarning('');
  }, [launchId]);

  // ── Scope — which requests compile/run actually look at. ──
  //
  // Two layers, in order: the Dashboard's row-picker handoff (when present and
  // not switched off), then this page's own type/host filter (ScopePanel).
  // The second layer is the fix for "no ability to select the requests to test
  // the acceptance criteria from" — previously the only control was the
  // handoff, and with none the page silently used the entire session.
  // A saved HAR (Batch → "From saved HAR") replaces the session as the base
  // rows and turns the handoff off: the tester asked to grade THAT capture,
  // so neither live traffic nor a Dashboard pick may leak into the run.
  // Held in memory only (a HAR can be thousands of rows) and cleared on a
  // launch switch.
  const [savedHar, setSavedHar] = useState(null); // { id, title, createdAt, rows }
  const [harPickerOpen, setHarPickerOpen] = useState(false);
  useEffect(() => { setSavedHar(null); }, [launchId]);
  const capturedRows = useMemo(() => Object.values(liveTrafficGroups || {}).flat(), [liveTrafficGroups]);
  const sessionRows = savedHar ? savedHar.rows : capturedRows;
  const pickedRows = useMemo(() => (Array.isArray(checksScopeRows) ? checksScopeRows : []), [checksScopeRows]);
  const handoffRows = savedHar ? EMPTY_ROWS : pickedRows;

  const [scope, setScope] = useState(() => readScopeState(launchId) || { types: [], hosts: [], platforms: [], useHandoff: true });
  useEffect(() => { setScope(readScopeState(launchId) || { types: [], hosts: [], platforms: [], useHandoff: true }); }, [launchId]);
  useEffect(() => { writeScopeState(launchId, scope); }, [launchId, scope]);

  const usingHandoff = handoffRows.length > 0 && scope.useHandoff;
  const baseRows = usingHandoff ? handoffRows : sessionRows;
  const scopedRows = useMemo(() => applyScope(baseRows, scope), [baseRows, scope]);
  const resolveRows = useCallback(() => scopedRows, [scopedRows]);

  // Who won each GAM request, for the report's "Requests graded" table — the same
  // Winner step the Slots view shows (reportWinners.js). Loaded once a run exists.
  const [reportWinners, setReportWinners] = useState(null);
  useEffect(() => {
    if (!runResults.length || !scopedRows.length) { setReportWinners(null); return undefined; }
    let live = true;
    loadReportLifecycles(scopedRows, { post: (rows) => postLifecycleGroups(rows, { manualOverrides: {} }), buildLocal: (rows) => buildSlotLifecycles(rows, {}) })
      .then((lcs) => { if (live) setReportWinners(winnersByGamRow(lcs)); })
      .catch(() => { if (live) setReportWinners(null); });
    return () => { live = false; };
  }, [runResults, scopedRows]);

  // What the tester EXPLICITLY picked, handed to the compiler as authoritative
  // scope (api/launches.js compileLaunch → the proxy's compileCriteria).
  const { types: scopeTypes, hosts: scopeHosts } = useMemo(
    () => deriveScopeDirective({ scope, handoffRows, usingHandoff }),
    [scope, handoffRows, usingHandoff],
  );

  const scopeFiltered = scope.types.length > 0 || scope.hosts.length > 0 || (Array.isArray(scope.platforms) && scope.platforms.length > 0);
  const scopeCaption = (usingHandoff || scopeFiltered)
    ? `over ${scopedRows.length} selected request${scopedRows.length === 1 ? '' : 's'}`
    : `over the full session${sessionRows.length ? ` (${sessionRows.length} request${sessionRows.length === 1 ? '' : 's'})` : ''}`;
  const sourceCaption = savedHar
    ? `${scopeCaption.replace('the full session', 'the whole HAR')} from saved HAR “${savedHar.title}”`
    : scopeCaption;

  const rowsByRequestId = useMemo(() => {
    const map = {};
    for (const r of scopedRows || []) if (r?.requestId) map[r.requestId] = r;
    return map;
  }, [scopedRows]);

  // Evidence HAR row source: every row this tab holds, not just the scoped
  // ones — a run that posts no rows grades the proxy's whole history.
  const evidenceRowsById = useMemo(() => {
    const map = {};
    for (const r of sessionRows || []) if (r?.requestId) map[r.requestId] = r;
    for (const r of handoffRows || []) if (r?.requestId) map[r.requestId] = r;
    return map;
  }, [sessionRows, handoffRows]);

  const batchResultsById = useMemo(() => {
    const map = {};
    for (const r of runResults || []) if (r?.checkId) map[r.checkId] = r;
    return map;
  }, [runResults]);

  const liveResultsById = useMemo(() => {
    const map = {};
    for (const r of liveResults || []) if (r?.checkId) map[r.checkId] = r;
    return map;
  }, [liveResults]);

  /**
   * The runs that have produced results, newest first. Two runs are kept
   * SEPARATE and labelled rather than merged: a batch run grades the requests
   * the tester selected, a live capture grades whatever the device is emitting
   * right now, and they legitimately disagree. Merging them would silently
   * pick a winner; this makes the disagreement readable instead.
   *
   * WP6 (docs/mcp-signoff-spec.md §6/§9): a source's presence here is now
   * gated on the SERVER'S rollup having recorded/attached runs for it
   * (`batchRollup`/`liveRollup`'s `runIds`), not on whether this browser tab
   * happens to be holding local result rows — those are ephemeral (evidence
   * only, never persisted) and reset on reload, while the rollup is
   * authoritative and survives it. `resultsByCheckId` still comes from the
   * local rows (batchResultsById/liveResultsById) because the rollup's own
   * checks[] is deliberately slim (no matched/found/rows) — it can tell you
   * the VERDICT but not render the evidence table.
   */
  const sources = useMemo(() => {
    const list = [];
    if (batchRollup && Array.isArray(batchRollup.runIds) && batchRollup.runIds.length > 0) {
      list.push({ key: 'batch', label: 'Batch run', at: batchRollup.runAt || null, serverRollup: batchRollup, resultsByCheckId: batchResultsById });
    }
    if (liveRollup && Array.isArray(liveRollup.runIds) && liveRollup.runIds.length > 0) {
      list.push({ key: 'live', label: 'Live capture', at: liveRollup.runAt || null, serverRollup: liveRollup, resultsByCheckId: liveResultsById });
    }
    return list.sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0));
  }, [batchRollup, liveRollup, batchResultsById, liveResultsById]);

  // An explicit pick wins; otherwise follow the freshest run.
  const activeSource = sources.find((s) => s.key === activeSourceKey) || sources[0] || null;
  const resultsByCheckId = activeSource ? activeSource.resultsByCheckId : {};
  // What the sign-off report says its verdict is about (buildLaunchReport
  // `capture`): the active result source and the same scope caption shown
  // beside the buttons, so the document can't be read against the wrong
  // traffic.
  const reportCapture = {
    label: activeSource ? activeSource.label : 'No run yet',
    at: activeSource?.at || null,
    scope: sourceCaption,
  };

  // Newest evidence snapshot for the active source (batch/live), else newest.
  const evidenceRun = useMemo(() => {
    const runs = Array.isArray(launch?.runs) ? launch.runs : [];
    const want = activeSource?.key;
    for (let i = runs.length - 1; i >= 0; i -= 1) if (!want || runs[i].mode === want) return runs[i];
    return runs[runs.length - 1] || null;
  }, [launch?.runs, activeSource?.key]);

  /**
   * Download the evidence HAR — one criterion's graded (or failing) rows, or
   * the whole launch's when criterionId is null. Prefers the HAR saved at run
   * time (complete bodies, survives reloads); falls back to building one from
   * the current results + the rows this tab still holds.
   */
  async function downloadEvidence(criterionId, failedOnly = false) {
    setEvidenceMsg('');
    try {
      let har = null;
      let missing = 0;
      if (evidenceRun?.harId) {
        har = await getHar(evidenceRun.harId);
      } else {
        const ev = criterionEvidence(launch?.criteria, resultsByCheckId);
        const built = buildEvidenceHar({ evidence: ev, rowsById: evidenceRowsById, meta: { launchId: launch?.id, ticket: launch?.ticket || '', app: launch?.app || '', build: launch?.build || '' } });
        har = built.har;
        missing = built.missingIds.length;
      }
      const crit = criterionId ? (launch?.criteria || []).find((c) => c.id === criterionId) : null;
      if (criterionId) {
        const sliced = sliceEvidenceHar(har, criterionId, { failedOnly });
        if (!sliced) { setEvidenceMsg('This run recorded no request evidence for that criterion — re-run checks to capture it.'); return; }
        har = sliced.har;
        missing = sliced.missingIds.length;
      }
      if (!har?.log?.entries?.length) { setEvidenceMsg('No captured requests to put in the HAR.'); return; }
      downloadHarFile(har, evidenceFilename({
        ticket: launch?.ticket, label: crit?.text, app: launch?.app,
        build: evidenceRun?.build || launch?.build, date: evidenceRun?.at, failedOnly,
      }));
      if (missing > 0) setEvidenceMsg(`Downloaded — ${missing} graded request${missing === 1 ? ' is' : 's are'} missing from it (no longer held; ids listed in the HAR).`);
    } catch (e) {
      setEvidenceMsg(`Could not download the HAR (${e?.message || 'unknown error'}).`);
    }
  }

  function jumpToRequest(requestId) {
    if (!requestId) return;
    setJumpTarget(requestId);
    navigate('/', { state: { from: '/checks?mode=signoff', fromLabel: 'Sign off' } });
  }

  async function persistLaunch(next) {
    const res = await saveLaunch(next);
    if (res?.ok && res?.launch) setLaunch(res.launch);
    return res;
  }

  async function handleSaveCriteriaText() {
    if (!launch) return;
    setSavingText(true);
    try {
      await persistLaunch({ ...launch, criteriaText: criteriaTextDraft });
    } catch (e) {
      setLoadError(e?.message || 'Could not save criteria text.');
    } finally {
      setSavingText(false);
    }
  }

  async function handleSaveManualNote(criterionId, note) {
    if (!launch) return;
    const nextCriteria = (launch.criteria || []).map((c) => (c.id === criterionId ? { ...c, manualNote: note } : c));
    try {
      await persistLaunch({ ...launch, criteria: nextCriteria });
    } catch (e) {
      setLoadError(e?.message || 'Could not save the note.');
    }
  }

  async function handleCompile() {
    if (!launch) return;
    setCompiling(true);
    setCompileError(null);
    setCompileNotes([]);
    try {
      const res = await compileLaunch(launch.id, {
        rows: scopedRows.length ? scopedRows : undefined,
        app: launch.app || undefined,
        adOnly,
        criteriaText: criteriaTextDraft,
        scopeTypes,
        scopeHosts,
      });
      if (res?.ok && res?.launch) {
        setLaunch(res.launch);
        setCriteriaTextDraft(res.launch.criteriaText || criteriaTextDraft);
        // A successful compile still reports what it DECIDED — which criteria
        // it narrowed to a vendor, which cited check it had to replace with a
        // scoped copy. Silently dropping those left the tester unable to tell
        // whether a criterion was verified against the traffic they meant.
        setCompileNotes(Array.isArray(res.issues) ? res.issues : []);
        // Re-compiling re-authors the checks (ids and scope can change), so the
        // previous run no longer describes what is now mapped. Drop it rather
        // than let a criterion show a verdict a different check produced.
        setRunResults([]); setLastRunId(null); setRunAt(null); setRunEvidenceDropped(false);
        clearRunState(launch.id);
        // A compile REPLACES the launch's suite — refetch so every criterion
        // shows what it now actually grades, not the previous compile's checks.
        loadCheckDefs();
        // The recompiled criteria/checkIds mean a previously-attached run may
        // now map differently (or not at all) — refetch the server rollup so
        // the verdict strip reflects the NEW suite rather than a stale read.
        refreshRollups();
        // Compile only decides WHICH checks grade each criterion and clears the old
        // run, so every compile used to leave the report at "Not run". In Batch mode
        // the run is deterministic and free: run it now over the same scope.
        const mapped = (res.launch.criteria || []).some((c) => (c?.checkIds || []).length);
        if (verifyMode === 'batch' && mapped) {
          setCompileNotes((n) => [...n, 'Checks ran automatically after compile.']);
          await handleRun(res.launch);
        }
      } else {
        setCompileError({ message: 'Could not compile these criteria.', issues: Array.isArray(res?.issues) ? res.issues : [] });
      }
    } catch (e) {
      setCompileError({ message: e?.message || 'Compile failed.', is409: is409ApiKey(e) });
    } finally {
      setCompiling(false);
    }
  }

  // Persist an edited check back to the launch's own suite (replace-by-id via
  // POST /proxy/checks/suites/<name>/append). Rejects on a validation failure
  // so CheckSpecPanel can render the proxy's own issues[] — a rejected edit
  // must not look like it saved. The next compile replaces the suite wholesale;
  // that is the documented lifetime of an edit, surfaced in the panel's copy.
  async function handleSaveCheck(next) {
    const suite = checkSuite || launch?.suite;
    if (!suite) throw new Error('This launch has no compiled suite yet — compile the criteria first.');
    await appendCheckToSuite(suite, next);
    loadCheckDefs();
  }

  // Run every mapped check for this launch and group results by criterion.
  // See the file-header comment for why this can be more than one
  // /proxy/checks/run call (suite + checks don't merge server-side).
  // `target` lets handleCompile run the launch it just received: `launch` in this
  // closure is still the pre-compile state until React re-renders.
  async function handleRun(target) {
    const launch = (target && target.id) ? target : launchRef.current;
    if (!launch) return;
    setRunning(true);
    setRunError('');
    setAttachWarning('');
    try {
      const rows = resolveRows();
      if (savedHar && !rows.length) {
        throw new Error('The scope filter leaves no requests in this saved HAR — widen the filter or pick another HAR.');
      }
      const base = {
        app: launch.app || undefined,
        rows: rows.length ? rows : undefined,
        selectionExplicit: usingHandoff || scopeFiltered || !!savedHar,
        ...(adOnly ? { adOnly: true } : {}),
      };

      // Primary run — the launch's own suite (authored checks). This is the
      // one that gets recorded to run history and attached to the launch.
      // mode: 'batch' (WP6, docs/mcp-signoff-spec.md §5) tags this run so the
      // proxy's launch rollup can tell it apart from CapturePanel's live
      // finalize run — both record with trigger:'run', so `trigger` alone
      // can't distinguish them.
      const primary = await runChecks({ suite: launch.suite, ...base, mode: 'batch', gradedIds: true });
      let merged = Array.isArray(primary?.results) ? [...primary.results] : [];
      const covered = new Set(merged.map((r) => r?.checkId).filter(Boolean));

      // Which existing-mapped checkIds does the primary run NOT already
      // cover? Post-polish this is normally EMPTY — compileCriteria now
      // copies every 'existing'-mapped check into the launch's own suite, so
      // the primary run above already covers it. Only a pre-polish launch
      // (compiled before that copy step shipped, suite has authored checks
      // only) falls through to the builtin-suite lookup below.
      // EVERY mapped id, not just an 'existing' record's: a criterion can now
      // carry authored checks AND cited/attached baseline ids together (hybrid
      // coverage), so keying the fallback off the mapping label would leave a
      // hybrid criterion's cited checks silently unrun on a pre-polish launch.
      const existingIds = new Set();
      for (const c of launch.criteria || []) {
        if (c?.mapping === 'manual' || c?.mapping === 'unmapped') continue;
        for (const id of c.checkIds || []) if (id) existingIds.add(id);
      }
      const missing = [...existingIds].filter((id) => !covered.has(id));

      if (missing.length) {
        for (const suiteName of BUILTIN_SUITES) {
          if (![...missing].some((id) => !covered.has(id))) break; // everything resolved already
          try {
            // eslint-disable-next-line no-await-in-loop
            const supplemental = await runChecks({ suite: suiteName, ...base, record: false, gradedIds: true });
            for (const r of Array.isArray(supplemental?.results) ? supplemental.results : []) {
              if (r?.checkId && existingIds.has(r.checkId) && !covered.has(r.checkId)) {
                merged.push(r);
                covered.add(r.checkId);
              }
            }
          } catch {
            // A lookup suite failing (e.g. transient) shouldn't fail the
            // whole run — those criteria just stay unresolved (Not run).
          }
        }
      }

      setRunResults(merged);
      const runId = primary?.runId ?? null;
      setLastRunId(runId);
      const at = new Date().toISOString();
      setRunAt(at);
      setRunEvidenceDropped(false);
      // Survive a round trip to the dashboard and back (see runStateKey).
      writeRunState(launch.id, { results: merged, runId, at });
      if (runId) {
        // Save the evidence HAR NOW (bodies age out of the proxy) and record
        // the run snapshot — POST /runs also attaches the run, replacing the
        // bare attachRun this used to call.
        setEvidenceMsg('');
        const ev = await persistRunEvidence({
          launch, results: merged, runId, mode: 'batch',
          rowsById: evidenceRowsById, saveHar: saveHarToServer, recordRun: recordLaunchRun,
        });
        if (ev.error) {
          // WP6: never silent — the displayed verdict comes from the server
          // rollup, which won't include this run until it is attached. Do NOT
          // fall back to computing locally here.
          setAttachWarning(
            `This run wasn't attached to the launch (${ev.error}) — the verdict below may not include it.`,
          );
        } else {
          if (ev.launch) setLaunch(ev.launch);
          // The server rollup only reflects ATTACHED runs — refetch now.
          refreshRollups();
        }
        setEvidenceMsg(evidenceStatusText(ev));
      }
    } catch (e) {
      setRunError(e?.message || 'Run failed.');
    } finally {
      setRunning(false);
    }
  }

  async function handleDelete() {
    if (!launch) return;
    if (!window.confirm(`Delete launch "${launch.title || launch.id}"? This also deletes its check suite.`)) return;
    try {
      await deleteLaunch(launch.id);
      clearRunState(launch.id);
      onDeleted();
    } catch (e) {
      setLoadError(e?.message || 'Delete failed.');
    }
  }

  if (loading) return <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress size={28} /></Stack>;
  if (loadError && !launch) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>Back to launches</Button>
        <Alert severity="error">{loadError}</Alert>
      </Box>
    );
  }
  if (!launch) return null;

  const criteria = Array.isArray(launch.criteria) ? launch.criteria : [];
  // WP6 (docs/mcp-signoff-spec.md §6/§9): the launch-level rollup counts are
  // the SERVER's, for the active source — not a local launchRollup() call.
  // Zeroed fallback so the strip (gated on sources.length>0 below) never
  // reads undefined mid-fetch.
  const rollup = activeSource?.serverRollup?.rollup
    || { met: 0, notMet: 0, needsReview: 0, manualReview: 0, notRun: 0 };
  const serverVerdict = activeSource?.serverRollup;
  const hasCompiled = criteria.length > 0;

  // What the last compile produced, from the stored record rather than from
  // the compile response — so it survives a reload and a round trip to the
  // dashboard. Re-compile used to leave the page looking identical whether it
  // had changed everything or nothing.
  const compileSummary = (() => {
    if (!hasCompiled) return null;
    const mappedIds = new Set();
    let manual = 0;
    let unmapped = 0;
    let partial = 0;
    for (const c of criteria) {
      if (c?.mapping === 'manual') manual += 1;
      else if (c?.mapping === 'unmapped') unmapped += 1;
      for (const id of (Array.isArray(c?.checkIds) ? c.checkIds : [])) mappedIds.add(id);
      if (Array.isArray(c?.coverage?.missing) && c.coverage.missing.length) partial += 1;
    }
    return { criteria: criteria.length, checks: mappedIds.size, manual, unmapped, partial };
  })();

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack}>Back to launches</Button>
        <IconButton size="small" onClick={handleDelete} title="Delete launch"><DeleteOutlineIcon fontSize="small" /></IconButton>
      </Stack>

      {loadError && <Alert severity="warning" sx={{ mb: 2 }}>{loadError}</Alert>}

      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{launch.title || '(untitled)'}</Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
          {launch.ticket && <Chip size="small" label={`Ticket: ${launch.ticket}`} />}
          {launch.app && <Chip size="small" label={`App: ${launch.app}`} />}
          {launch.build && <Chip size="small" label={`Build: ${launch.build}`} />}
          <Chip size="small" variant="outlined" label={`Updated ${fmtDate(launch.updatedAt)}`} />
          {reviewTimeLabel(launch.review) && <Chip size="small" variant="outlined" label={reviewTimeLabel(launch.review)} />}
        </Stack>
        {reviewMedianLabel(reviewSummary) && (
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: reviewWithinTarget(reviewSummary) === false ? 'var(--warn-text)' : 'var(--muted)' }}>
            {reviewMedianLabel(reviewSummary)}
          </Typography>
        )}
      </Paper>

      {/* ── Request scope (which traffic compile/run look at) ── */}
      <ScopePanel
        sessionRows={sessionRows}
        handoffRows={handoffRows}
        scope={scope}
        onChange={setScope}
        scopedCount={scopedRows.length}
      />

      {/* ── Criteria text + compile ── */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Acceptance criteria</Typography>
        <TextField
          value={criteriaTextDraft}
          onChange={(e) => setCriteriaTextDraft(e.target.value)}
          fullWidth
          multiline
          minRows={5}
          placeholder="Paste the feature's acceptance criteria, one per line…"
        />
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            variant="outlined"
            disabled={savingText || criteriaTextDraft === (launch.criteriaText || '')}
            onClick={handleSaveCriteriaText}
          >
            {savingText ? 'Saving…' : 'Save text'}
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={compiling || !criteriaTextDraft.trim()}
            onClick={handleCompile}
            startIcon={compiling ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            {compiling ? 'Compiling…' : hasCompiled ? 'Re-compile' : 'Compile criteria'}
          </Button>
          <FormControlLabel
            control={<Switch size="small" checked={adOnly} onChange={(e) => setAdOnly(e.target.checked)} />}
            label={<Typography variant="caption">ad traffic only</Typography>}
          />
          <Typography variant="caption" color="text.secondary">{sourceCaption}</Typography>
        </Stack>

        {!hasCompiled && !criteriaTextDraft.trim() && (
          <Alert severity="info" icon={false} sx={{ mt: 1.5, py: 0.5 }}>
            Paste this feature's acceptance criteria above, then Compile.
          </Alert>
        )}

        {/* What the compile produced. Without this, "Re-compile" gave no
            feedback at all — same page, same criteria, no way to tell whether
            anything changed or how much of the paste is actually covered. */}
        {compileSummary && (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
            <Chip
              size="small"
              label={`compiled: ${compileSummary.criteria} criteria → ${compileSummary.checks} check${compileSummary.checks === 1 ? '' : 's'}`}
            />
            {compileSummary.manual > 0 && <Chip size="small" variant="outlined" label={`${compileSummary.manual} manual`} />}
            {compileSummary.unmapped > 0 && <Chip size="small" variant="outlined" color="warning" label={`${compileSummary.unmapped} unmapped`} />}
            {compileSummary.partial > 0 && (
              <Chip size="small" variant="outlined" color="warning" label={`${compileSummary.partial} partly verified`} />
            )}
            <Typography variant="caption" color="text.secondary">
              expand a check below to see exactly what it grades — and edit it
            </Typography>
          </Stack>
        )}

        {compileNotes.length > 0 && (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            How these criteria were mapped:
            <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2.5 }}>
              {compileNotes.map((note, i) => <li key={i}><Typography variant="caption">{String(note)}</Typography></li>)}
            </Box>
          </Alert>
        )}

        {compileError && (
          <Alert severity={compileError.is409 ? 'warning' : 'error'} sx={{ mt: 1.5 }}>
            {compileError.is409
              ? <>Set <code>ANTHROPIC_API_KEY</code> on the proxy to compile criteria.</>
              : compileError.message}
            {Array.isArray(compileError.issues) && compileError.issues.length > 0 && (
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
                {compileError.issues.map((iss, i) => <li key={i}><Typography variant="caption">{String(iss)}</Typography></li>)}
              </Box>
            )}
          </Alert>
        )}
      </Paper>

      {/* ── Run ── */}
      {/* ── Verify: ONE panel, two modes ─────────────────────────────────────
          Batch and Live are two ways to produce the same verdicts — grade the
          traffic already captured, or tap through the app and let the criteria
          fill in. They shipped as two separate cards ("Run" and "Capture") from
          two different specs, stacked as if they were sequential steps, each
          with its own result store and its own copy of the criteria list. The
          result: one criterion reading "Waiting / Not run" in Capture and "Met"
          in the card below it, simultaneously, with nothing explaining which to
          believe. One panel, one criteria list, and every verdict labelled with
          the run it came from (see `sources` below). ── */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Verify</Typography>
            {rollupsLoading && <CircularProgress size={12} />}
          </Stack>
          <Typography variant="caption" color="text.secondary">{scopeCaption}</Typography>
        </Stack>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={verifyMode}
          onChange={(e, v) => { if (v) setVerifyMode(v); }}
          sx={{ mb: 1.5 }}
        >
          <ToggleButton value="batch">Batch — grade captured traffic</ToggleButton>
          <ToggleButton value="live" disabled={!hasCompiled}>Live — tap through the app</ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ display: verifyMode === 'batch' ? 'block' : 'none' }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
            <Typography variant="caption" color="text.secondary">Grade:</Typography>
            {savedHar ? (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={`Saved HAR: ${savedHar.title} · ${savedHar.rows.length} request${savedHar.rows.length === 1 ? '' : 's'}`}
                onDelete={() => setSavedHar(null)}
              />
            ) : (
              <Chip size="small" variant="outlined" label={usingHandoff ? 'Dashboard selection' : 'Captured traffic'} />
            )}
            <Button size="small" onClick={() => setHarPickerOpen(true)}>
              {savedHar ? 'Pick another HAR' : 'From saved HAR…'}
            </Button>
          </Stack>
          <SavedHarPicker
            open={harPickerOpen}
            onClose={() => setHarPickerOpen(false)}
            onPick={(h) => { setSavedHar(h); setVerifyMode('batch'); }}
          />
          <Button
            variant="contained"
            disabled={running || !hasCompiled}
            onClick={() => handleRun()}
            startIcon={running ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            {running ? 'Running…' : 'Run checks'}
          </Button>
          {!hasCompiled && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Compile criteria first — nothing mapped to run yet.
            </Typography>
          )}
          {runError && <Alert severity="error" sx={{ mt: 1.5 }}>{runError}</Alert>}
          {/* WP6: attach-run failing is no longer a silent catch (see
              handleRun) — this run exists locally but the server rollup
              below won't know about it until attach succeeds. */}
          {attachWarning && <Alert severity="warning" sx={{ mt: 1.5 }}>{attachWarning}</Alert>}
          {(lastRunId || runAt) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Last run{runAt ? ` ${fmtDate(runAt)}` : ''}{lastRunId ? <> · <code>{lastRunId}</code></> : ''}
              {runEvidenceDropped ? ' · evidence rows not kept (large run) — re-run to inspect them' : ''}
            </Typography>
          )}
          {(evidenceRun?.harId || runResults.length > 0) && (
            <Button size="small" sx={{ mt: 1 }} onClick={() => downloadEvidence(null)}>
              Download evidence HAR
            </Button>
          )}

        </Box>

        {/* HIDDEN, not unmounted: a capture session owns a WebSocket
            subscription, a 2s clock and a quiet timer. Flipping to Batch to
            read a verdict must not silently kill a capture that is mid-run. */}
        {hasCompiled && (
          <Box sx={{ display: verifyMode === 'live' ? 'block' : 'none' }}>
            <CapturePanel
              key={launch.id}
              launch={launch}
              onLaunchUpdated={(l) => { setLaunch(l); refreshRollups(); }}
              tester={readTester()}
              onLiveResults={handleLiveResults}
              evidenceRowsById={evidenceRowsById}
            />
          </Box>
        )}

        {/* WP5 (docs/launch-signoff-spec.md §7): "Generate sign-off report" —
            self-contained HTML mirroring buildSignOffReport.js, with a
            per-criterion Met/Not met/Needs review/Manual review/Not run
            verdict banner + acknowledgement-gated sign-off. Enabled once the
            launch has at least one criterion; resultsByCheckId is passed
            even before any run so the report is still honest (everything
            renders 'not run').

            Shared by both modes — a sign-off is a sign-off however the traffic
            was produced. It reports the ACTIVE source, named in the caption, so
            the document can't silently be built from the run you weren't
            looking at. */}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: hasCompiled ? 1.5 : 0 }}>
          <Button
            size="small"
            variant="contained"
            disabled={criteria.length === 0}
            onClick={() => { markSigned(launch.id); openLaunchReport(launch, resultsByCheckId, { tester: readTester(), runId: lastRunId || '', app: launch.app || '', checkDefsById, capture: reportCapture, rows: resolveRows(), winners: reportWinners, serverRollup: activeSource?.serverRollup || null }); }}
          >
            Generate sign-off report
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={criteria.length === 0}
            onClick={() => { markSigned(launch.id); downloadLaunchReport(launch, resultsByCheckId, { tester: readTester(), runId: lastRunId || '', app: launch.app || '', checkDefsById, capture: reportCapture, rows: resolveRows(), winners: reportWinners, serverRollup: activeSource?.serverRollup || null }); }}
          >
            Download
          </Button>
          {activeSource && (
            <Typography variant="caption" color="text.secondary">
              from {activeSource.label}{activeSource.at ? ` ${fmtTime(activeSource.at)}` : ''}
            </Typography>
          )}
        </Stack>
      </Paper>

      {/* C21 / T34: the proxy's answer is what this page shows. When neither rollup fetch answered but this tab
          holds local results, say so instead of silently showing nothing (selectRollup is the pure decision). */}
      {!rollupsLoading && Object.keys(batchResultsById).length + Object.keys(liveResultsById).length > 0 && (() => {
        const sel = selectRollup(batchRollup || liveRollup, null);
        return sel.origin === 'proxy' ? null : <Alert severity="warning" sx={{ mb: 1.5 }} data-testid="rollup-fallback-notice">{sel.notice}</Alert>;
      })()}
      {waiveTarget && (
        <Box sx={{ mb: 2 }}>
          <WaiveDialog
            minLength={10}
            target={{ criterion: waiveTarget.criterionId, unit: waiveTarget.unitId }}
            error={waiveError}
            onSubmit={handleWaiveSubmit}
            onCancel={() => { setWaiveTarget(null); setWaiveError(''); }}
          />
        </Box>
      )}

      {/* ── Rollup strip — always labelled with the run it was computed from,
          so a number can never be read against the wrong run. ── */}
      {sources.length > 0 && (
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Chip label={`Met: ${rollup.met}`} color="success" variant={rollup.met ? 'filled' : 'outlined'} sx={{ fontWeight: 700 }} />
          <Chip label={`Not met: ${rollup.notMet}`} color="error" variant={rollup.notMet ? 'filled' : 'outlined'} sx={{ fontWeight: 700 }} />
          <Chip label={`Needs review: ${rollup.needsReview}`} color="warning" variant={rollup.needsReview ? 'filled' : 'outlined'} sx={{ fontWeight: 700 }} />
          <Chip label={`Manual review: ${rollup.manualReview}`} color="info" variant={rollup.manualReview ? 'filled' : 'outlined'} sx={{ fontWeight: 700 }} />
          <Chip label={`Not run: ${rollup.notRun}`} variant={rollup.notRun ? 'filled' : 'outlined'} sx={{ fontWeight: 700 }} />
          {/* WP6: signable is the server's own, stricter than the verdict
              alone (verdict='met' but coverage incomplete is still NOT
              signable — see buildRollup.js). unsignableReason/blockers come
              straight off the response; not re-derived here. */}
          {serverVerdict && (
            <Chip
              size="small"
              label={serverVerdict.signable ? 'Signable' : 'Not signable'}
              color={serverVerdict.signable ? 'success' : 'default'}
              variant={serverVerdict.signable ? 'filled' : 'outlined'}
            />
          )}
          <Typography variant="caption" color="text.secondary">
            from {activeSource?.label}{activeSource?.at ? ` ${fmtDate(activeSource.at)}` : ''}
            {serverVerdict && !serverVerdict.signable && (
              serverVerdict.unsignableReason
                ? ` · ${serverVerdict.unsignableReason.replace(/_/g, ' ')}`
                : Array.isArray(serverVerdict.blockers) && serverVerdict.blockers.length > 0
                  ? ` · ${serverVerdict.blockers.length} blocker${serverVerdict.blockers.length === 1 ? '' : 's'}`
                  : ''
            )}
          </Typography>
        </Stack>
      )}

      {/* Per-user isolation (AC 123): the run's owner scope. Same sentences, from the same
          functions, as the report's "Limits of this capture" (reportWording.limitsOfCapture). */}
      {sources.length > 0 && <RunScopeNotes scope={serverVerdict?.scope} unsignableReason={serverVerdict?.unsignableReason} />}
      <DigestCard launchId={launch.id} refreshKey={launch.runs?.length} />

      {/* ── Criteria (results grouped BY CRITERION) ── */}
      <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Criteria ({criteria.length})</Typography>
        {/* Both runs stay visible and stay labelled. When a batch run and a
            live capture disagree about a criterion, the card shows BOTH
            verdicts; this picks whose evidence the detail below is drawn
            from, rather than silently merging two runs into one number. */}
        {sources.length > 1 && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">showing evidence from</Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={activeSourceKey}
              onChange={(e, v) => { if (v) setActiveSourceKey(v); }}
            >
              {sources.map((s) => (
                <ToggleButton key={s.key} value={s.key} sx={{ textTransform: 'none' }}>
                  {s.label}{s.at ? ` · ${fmtTime(s.at)}` : ''}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>
        )}
      </Stack>
      {evidenceMsg && <Alert severity="info" sx={{ mb: 1.5 }} onClose={() => setEvidenceMsg('')}>{evidenceMsg}</Alert>}
      {criteria.length === 0 ? (
        <Typography variant="body2" color="text.secondary">None yet.</Typography>
      ) : (
        criteria.map((c) => (
          <CriterionCard
            key={c.id}
            criterion={c}
            resultsByCheckId={resultsByCheckId}
            sources={sources}
            activeSourceKey={activeSource?.key}
            checkDefsById={checkDefsById}
            onJump={jumpToRequest}
            onSaveNote={handleSaveManualNote}
            onSaveCheck={handleSaveCheck}
            app={launch.app}
            runId={lastRunId}
            launch={launch}
            rowsByRequestId={rowsByRequestId}
            suiteName={launch.suite}
            onDownloadEvidence={downloadEvidence}
            evidenceCounts={(evidenceRun?.criteria || []).find((e) => e.id === c.id) || null}
            onWaive={(criterionId, unitId) => { setWaiveError(''); setWaiveTarget({ criterionId, unitId }); }}
          />
        ))
      )}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// LaunchPage — top-level list/create/detail switch
// ─────────────────────────────────────────────────────────────────────────
export default function LaunchPage({ embedded = false } = {}) {
  const [selectedId, setSelectedId] = useState(() => {
    try { return sessionStorage.getItem(SELECTED_LAUNCH_KEY) || null; } catch { return null; }
  });
  const [creating, setCreating] = useState(false);
  const [listBump, setListBump] = useState(0);

  useEffect(() => {
    try {
      if (selectedId) sessionStorage.setItem(SELECTED_LAUNCH_KEY, selectedId);
      else sessionStorage.removeItem(SELECTED_LAUNCH_KEY);
    } catch { /* quota/storage unavailable — non-fatal, view just won't persist */ }
  }, [selectedId]);

  function openLaunch(id) {
    setCreating(false);
    setSelectedId(id);
  }
  function backToList() {
    setSelectedId(null);
    setCreating(false);
    setListBump((b) => b + 1);
  }

  return (
    // `embedded`: rendered as the Sign off mode inside ChecksPage, which owns
    // the page padding. The standalone /launch route redirects there.
    <Box sx={embedded ? undefined : { p: 3, maxWidth: 1100, mx: 'auto' }}>
      {!selectedId && !creating && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Paste a feature's acceptance criteria, compile them into checks, run against
            captured traffic, and get a criterion-by-criterion verdict — Met, Not met, Needs
            review, or Manual review.
          </Typography>
          <LaunchList key={listBump} onOpen={openLaunch} onCreate={() => setCreating(true)} />
        </>
      )}
      {creating && (
        <CreateLaunchForm onCancel={() => setCreating(false)} onCreated={(id) => openLaunch(id)} />
      )}
      {selectedId && !creating && (
        <LaunchDetail launchId={selectedId} onBack={backToList} onDeleted={backToList} />
      )}
    </Box>
  );
}
